// PasoDocumentos.jsx
//
// #1809 · documentos antes de suscribirse (Sebastián, 30-oct-2026). Vive DENTRO del bloque de pago (`PlanesYPago`),
// así cubre de una vez los tres lugares donde se paga: «Elegir plan» (CheckoutDialog), el aviso de cobro
// programado (AvisoPagoDialog / `?pagar=1`) y la pantalla de bloqueo por prueba vencida (SuscripcionGate).
// Quién lo ve lo decide el backend (`documentos.exige`, helpers/documentos-pago.js).
//
// Orden «Tus documentos · Suscripción» (Sebastián, 31-oct-2026): paso 1 de 2 dentro del mismo diálogo, con la subida ahí
// mismo (`PasoDocumentos`). Es REUTILIZABLE: no sabe nada de planes ni de rieles. Recibe `documentos` (lo que
// devuelve `documentosDelCheckout`), un `resumen` opcional (la línea «Tu suscripción · Plan Max · $149.900 al mes») y `onEnviados`; cualquier
// otro flujo de pago (por ejemplo el de la jornada suelta) lo monta igual con lo que diga el back.
// PENDING paga igual: el pago no espera la revisión humana (72 h, alta_self_service_google).
// Cubre R10 — .claude/specs/1809-docs-pago/
import { useState } from "react";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import SubidaArchivo from "../SubidaArchivo";
import { EtiquetaCampo, campoSx } from "../../views/profile/PasosBienvenida";
import getClient from "../../helpers/get-client";
import { SOPORTE } from "../../helpers/soporte";
import { useCurrentUser } from "../context/UserContext";
import { botonPrimarioSx } from "./botonesSx";

const RNPI_URL = "https://rnpi.superdesalud.gob.cl/";

/**
 * «1 Tus documentos · 2 Suscripción» (Sebastián, 31-oct-2026: «para continuar usando smartdx de manera segura»
 * primero los papeles, después «selecciona la suscripción que más te acomode»; sin la palabra «pagar»). Solo aparece cuando a esta cuenta se le piden los papeles: sin
 * documentos no hay un paso intermedio que explicar y el bloque queda como siempre, sin indicador.
 */
// Cubre R73 — .claude/specs/jornada-suelta/: `rotuloPaso2` deja el «Suscripción» de los planes (#1809) y la compra de
// jornadas adicionales pasa «Confirmas» (esa pantalla dice «Sin suscripción» y no cobra mensual; tampoco dice «Pago»).
export const PasosPago = ({ paso, rotuloPaso2 = "Suscripción" }) => {
  const Punto = ({ n, label, activo, hecho }) => (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.75 }}>
      <Box
        sx={{
          width: 22,
          height: 22,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          fontSize: 12,
          fontWeight: 800,
          color: hecho || activo ? "var(--text-on-primary)" : "var(--text-muted)",
          backgroundColor: hecho ? "var(--color-success)" : activo ? "var(--color-primary)" : "var(--bg-input)",
          border: hecho || activo ? 0 : "1px solid var(--border-strong)",
        }}
      >
        {hecho ? <CheckCircleOutlineIcon sx={{ fontSize: 15 }} /> : n}
      </Box>
      <Typography
        component="span"
        sx={{ fontSize: 12.5, fontWeight: activo ? 800 : 600, color: activo ? "var(--text-primary)" : "var(--text-muted)" }}
      >
        {label}
      </Typography>
    </Box>
  );
  return (
    <Box data-testid="pago-pasos" data-paso={paso} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.75 }}>
      <Punto n={1} label="Tus documentos" activo={paso === 1} hecho={paso > 1} />
      <Box sx={{ flex: "1 1 8px", minWidth: 8, height: "1px", backgroundColor: "var(--border-strong)" }} />
      <Punto n={2} label={rotuloPaso2} activo={paso === 2} />
    </Box>
  );
};

/** Tras enviarlos (o si ya estaban en revisión): una línea, y el pago sigue abajo. */
export const DocumentosEnviados = ({ recien }) => (
  <Box
    data-testid="pago-docs-enviados"
    sx={{
      display: "flex",
      gap: 1,
      alignItems: "flex-start",
      p: "10px 12px",
      mb: 1.5,
      borderRadius: "12px",
      backgroundColor: "rgba(var(--color-success-rgb), 0.10)",
      border: "1px solid rgba(var(--color-success-rgb), 0.35)",
    }}
  >
    <CheckCircleOutlineIcon sx={{ fontSize: 18, color: "var(--color-success)", mt: "1px" }} />
    <Typography sx={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
      <Box component="span" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>
        {recien ? "Documentos enviados." : "Tus documentos están en revisión."}
      </Box>{" "}
      Los revisamos dentro de 72 horas. No tienes que esperar: ya puedes continuar.
    </Typography>
  </Box>
);

const CajaRechazo = ({ nota }) => (
  <Box
    data-testid="pago-docs-rechazados"
    sx={{
      display: "flex",
      gap: 1,
      alignItems: "flex-start",
      p: "10px 12px",
      mb: 1.5,
      borderRadius: "12px",
      backgroundColor: "rgba(var(--color-warning-rgb), 0.08)",
      border: "1px solid rgba(var(--color-warning-rgb), 0.35)",
    }}
  >
    <ErrorOutlineOutlinedIcon sx={{ fontSize: 18, color: "var(--color-warning)", mt: "1px" }} />
    <Typography sx={{ fontSize: 12.5, color: "var(--text-secondary)", lineHeight: 1.5 }}>
      <Box component="span" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>
        No pudimos validar tus documentos.
      </Box>{" "}
      {nota ? `${nota} ` : ""}Súbelos de nuevo y continúas.
    </Typography>
  </Box>
);

/**
 * La subida va acá mismo. Manda al MISMO endpoint del wizard (`/api/user/profile/documents/`,
 * `ProfileDocumentsSerializer`): el back exige carnet + N° de registro + certificado al titulado, y carnet +
 * universidad al interno/estudiante. Ese endpoint deja la cuenta en PENDING y no toca `trial_ends_at` (R7 de
 * #1401), así que subir papeles no reabre una prueba vencida: el bloqueo sigue y lo levanta el pago.
 */
const PasoDocumentos = ({ documentos, resumen, onEnviados, rotuloPaso2 }) => {
  const { currentUser, refreshUser } = useCurrentUser();
  const profile = currentUser?.professional_profile;
  const soloCarnet = documentos.pide === "solo-carnet";
  const [registro, setRegistro] = useState(profile?.registration_number || "");
  const [universidad, setUniversidad] = useState(profile?.university || "");
  const [carnet, setCarnet] = useState(null);
  const [certificado, setCertificado] = useState(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState("");
  const completo = Boolean(
    carnet && (soloCarnet ? universidad.trim() : registro.trim() && certificado)
  );

  const enviar = async () => {
    if (!completo || enviando) return;
    setEnviando(true);
    setError("");
    try {
      const payload = new FormData();
      // La profesión que el back dedujo (de la ficha o de la etapa del alta): sin ella, un interno sin profesión
      // guardada se validaría como titulado y el back le pediría el N° de registro.
      if (documentos.profesion) payload.append("profession", documentos.profesion);
      if (soloCarnet) payload.append("university", universidad.trim());
      else payload.append("registration_number", registro.trim());
      payload.append("id_document", carnet);
      if (!soloCarnet && certificado) payload.append("health_certificate", certificado);
      await getClient().post("/api/user/profile/documents/", payload, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await refreshUser?.().catch(() => {});
      onEnviados();
    } catch (err) {
      const data = err?.response?.data;
      setError(
        data && typeof data === "object"
          ? Object.values(data).flat().join(" · ")
          : `No pudimos enviar tus documentos. Intenta de nuevo o escríbenos a ${SOPORTE}.`
      );
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Box data-testid="pago-docs-paso" data-pide={documentos.pide}>
      <PasosPago paso={1} rotuloPaso2={rotuloPaso2} />
      <Typography sx={{ fontSize: 17, fontWeight: 800, color: "var(--text-primary)", lineHeight: 1.3 }}>
        Para seguir usando SmartDx de forma segura
      </Typography>
      <Typography sx={{ fontSize: 12.8, color: "var(--text-secondary)", mt: 0.5, mb: 1.5, lineHeight: 1.5 }}>
        {soloCarnet
          ? "Verificamos que eres estudiante o interno de salud: sube tu cédula e indica tu universidad. Te toma un minuto."
          : "Verificamos que eres profesional de la salud: sube tu cédula, tu N° de registro y tu certificado de la Superintendencia. Te toma dos minutos."}
      </Typography>
      {resumen && (
        <Box
          data-testid="pago-docs-resumen"
          sx={{
            display: "flex",
            gap: 1,
            p: "9px 12px",
            mb: 1.5,
            borderRadius: "12px",
            backgroundColor: "var(--bg-input)",
            border: "1px solid var(--border-soft)",
            fontSize: 12.5,
            fontVariantNumeric: "tabular-nums",
          }}
        >
          <Box component="span" sx={{ fontWeight: 700, color: "var(--text-primary)" }}>{resumen}</Box>
        </Box>
      )}
      {documentos.estado === "rechazados" && <CajaRechazo nota={documentos.notaRechazo} />}
      <Box sx={{ display: "grid", gap: "10px" }}>
        {soloCarnet ? (
          <Box>
            <EtiquetaCampo id="pago-universidad" nota="Donde cursas la carrera o el internado">
              Universidad
            </EtiquetaCampo>
            <Box
              component="input"
              id="pago-universidad"
              data-testid="pago-docs-universidad"
              sx={campoSx}
              placeholder="Ej: Universidad de Chile"
              value={universidad}
              onChange={(e) => setUniversidad(e.target.value)}
            />
          </Box>
        ) : (
          <Box>
            <EtiquetaCampo
              id="pago-registro"
              nota={
                <Box
                  component="a"
                  href={RNPI_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ color: "var(--accent-onboarding)", textDecoration: "underline", textUnderlineOffset: "3px", fontSize: 13 }}
                >
                  Dónde lo encuentro
                </Box>
              }
            >
              N° Registro Nacional de Prestadores
            </EtiquetaCampo>
            <Box
              component="input"
              id="pago-registro"
              data-testid="pago-docs-registro"
              sx={campoSx}
              placeholder="Ej: 234567"
              value={registro}
              onChange={(e) => setRegistro(e.target.value)}
            />
          </Box>
        )}
        <SubidaArchivo
          testId="pago-subida-carnet"
          titulo="Cédula de identidad (ambas caras)"
          detalle="Foto o PDF · máx 5 MB · las dos caras pueden ir en un mismo archivo"
          value={carnet}
          onChange={setCarnet}
        />
        {!soloCarnet && (
          <SubidaArchivo
            testId="pago-subida-certificado"
            titulo="Certificado de la Superintendencia"
            detalle="Lo descargas del RNPI · PDF o foto · máx 5 MB"
            value={certificado}
            onChange={setCertificado}
          />
        )}
      </Box>
      {error && (
        <Typography role="alert" data-testid="pago-docs-error" sx={{ fontSize: 12.5, color: "var(--color-danger)", mt: 1 }}>
          {error}
        </Typography>
      )}
      {enviando && <LinearProgress sx={{ mt: 1.25 }} />}
      <Button
        data-testid="pago-docs-enviar"
        variant="contained"
        disableElevation
        fullWidth
        disabled={!completo || enviando}
        onClick={enviar}
        sx={{ ...botonPrimarioSx, py: 1.4, fontSize: 14, mt: 1.75 }}
      >
        {enviando ? "Enviando..." : "Enviar y continuar"}
      </Button>
      <Box sx={{ display: "flex", gap: 0.75, alignItems: "center", mt: 1.1, color: "var(--text-faint)" }}>
        <LockOutlinedIcon sx={{ fontSize: 14 }} />
        <Typography sx={{ fontSize: 11.5, lineHeight: 1.45 }}>
          Solo los ve el equipo de SmartDx para validar que eres profesional de la salud.
        </Typography>
      </Box>
    </Box>
  );
};

export default PasoDocumentos;
